// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import {
  Copy,
  ChevronsUp,
  ChevronsDown,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Plus,
  Minus,
  Combine,
  SquareDashed,
  Scissors,
} from "lucide-react";

const IconBtn = ({ onClick, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="p-1.5 rounded text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-600 disabled:text-gray-300 dark:disabled:text-slate-600 disabled:hover:bg-transparent"
  >
    {children}
  </button>
);

const LabelBtn = ({ onClick, disabled, title, icon: Icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-600 disabled:text-gray-300 dark:disabled:text-slate-600 disabled:hover:bg-transparent"
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
  </button>
);

const Divider = () => (
  <span className="w-px h-5 bg-gray-200 dark:bg-slate-600 mx-1" />
);

export default function GeometryOpsBar({
  hasSelection,
  canBoolean,
  onDuplicate,
  onChangeZOrder,
  onBoolean,
  showNodeOps,
  hasActiveNode,
  onReversePath,
  onDuplicateNode,
  onDeleteNode,
}) {
  if (!hasSelection && !showNodeOps) return null;

  return (
    <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-0.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg px-1.5 py-1">
      {hasSelection && (
        <>
          <IconBtn onClick={onDuplicate} title="Duplicate (Ctrl+D)">
            <Copy className="w-4 h-4" />
          </IconBtn>
          <IconBtn
            onClick={() => onChangeZOrder("front")}
            title="Bring to front"
          >
            <ChevronsUp className="w-4 h-4" />
          </IconBtn>
          <IconBtn onClick={() => onChangeZOrder("back")} title="Send to back">
            <ChevronsDown className="w-4 h-4" />
          </IconBtn>
          <IconBtn onClick={() => onChangeZOrder("raise")} title="Raise one">
            <ArrowUp className="w-4 h-4" />
          </IconBtn>
          <IconBtn onClick={() => onChangeZOrder("lower")} title="Lower one">
            <ArrowDown className="w-4 h-4" />
          </IconBtn>
        </>
      )}

      {canBoolean && (
        <>
          <Divider />
          <LabelBtn
            onClick={() => onBoolean("union")}
            title="Union — merge shapes"
            icon={Combine}
            label="Union"
          />
          <LabelBtn
            onClick={() => onBoolean("intersection")}
            title="Intersection — keep overlap"
            icon={SquareDashed}
            label="Intersect"
          />
          <LabelBtn
            onClick={() => onBoolean("difference")}
            title="Difference — subtract upper shapes from the base"
            icon={Scissors}
            label="Difference"
          />
        </>
      )}

      {showNodeOps && (
        <>
          <Divider />
          <IconBtn onClick={onReversePath} title="Reverse path direction">
            <RotateCcw className="w-4 h-4" />
          </IconBtn>
          <IconBtn
            onClick={onDuplicateNode}
            disabled={!hasActiveNode}
            title="Duplicate active node"
          >
            <Plus className="w-4 h-4" />
          </IconBtn>
          <IconBtn
            onClick={onDeleteNode}
            disabled={!hasActiveNode}
            title="Delete active node"
          >
            <Minus className="w-4 h-4" />
          </IconBtn>
        </>
      )}
    </div>
  );
}
